import { getCountryByIP } from '@/utils/utils';

import type { Currency } from '@/constants/biling';
import type { EditorInstance } from 'novel';
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { useGenerateDefaultChatbot } from './useGenerateDefaultChatbot';

const UnriddleContext = createContext<{
  showAccessRevokedModal: boolean;
  setShowAccessRevokedModal: (show: boolean) => void;
  editor: EditorInstance | null;
  setEditor: (editor: EditorInstance) => void;
  editorContainerRef: React.RefObject<HTMLDivElement | null>;
  userCountry: Currency;
  showUgcProgramModal: boolean;
  setShowUgcProgramModal: (show: boolean) => void;
  showFreeUserModal: boolean;
  setShowFreeUserModal: (show: boolean) => void;
}>({
  showAccessRevokedModal: false,
  setShowAccessRevokedModal: () => {},
  editor: null,
  setEditor: (_editor: EditorInstance) => {},
  editorContainerRef: { current: null },
  userCountry: 'US',
  showUgcProgramModal: false,
  setShowUgcProgramModal: () => {},
  showFreeUserModal: false,
  setShowFreeUserModal: () => {},
});

export const UnriddleContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { user } = useAuth();

  const [showAccessRevokedModal, setShowAccessRevokedModal] = useState(false);
  const [editor, setEditor] = useState<EditorInstance | null>(null);
  const [userCountry, setUserCountry] = useState<Currency>('US');
  const [showUgcProgramModal, setShowUgcProgramModal] = useState(false);
  const [showFreeUserModal, setShowFreeUserModal] = useState(false);

  const editorContainerRef = useRef<HTMLDivElement | null>(null);

  useGenerateDefaultChatbot();

  // Shows access revoked for revoked teams users
  useEffect(() => {
    if (
      user &&
      user.teamsMemberOf.length > 0 &&
      user.teamsMemberOf[0].status === 'REVOKED'
    ) {
      setShowAccessRevokedModal(true);
    }
  }, [user]);

  useEffect(() => {
    const fetchUserCountry = async () => {
      const country = await getCountryByIP();
      setUserCountry(country);
    };

    fetchUserCountry();
  }, [user]);

  return (
    <UnriddleContext.Provider
      value={{
        showAccessRevokedModal,
        setShowAccessRevokedModal,
        editor,
        setEditor,
        editorContainerRef,
        userCountry,
        showUgcProgramModal,
        setShowUgcProgramModal,
        showFreeUserModal,
        setShowFreeUserModal,
      }}
    >
      {children}
    </UnriddleContext.Provider>
  );
};

export const useUnriddle = () => {
  return useContext(UnriddleContext);
};
